/**
 * GroupedResultsList Component
 *
 * Displays search results organized by project with premium styling.
 * Features collapsible sections, project watchlist toggle, and smooth animations.
 * Uses composite keys (projectId-itemId) for watchlist to prevent ID collisions.
 */

import {
  AppstoreAddOutlined,
  DownOutlined,
  FolderOutlined,
  LoadingOutlined,
  RightOutlined,
  SearchOutlined,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { buildSymbolStatusKey, type SymbolStatusMap } from '@/entities/cex';
import { createWatchlistKey, type ProjectItems } from '@/entities/watchlist';

import { ArbitrageResultItem } from './ArbitrageResultItem';
import { BalancerResultItem } from './BalancerResultItem';
import { SearchResultItem } from './SearchResultItem';

import type {
  ArbitrageSearchResult,
  BalancerSearchResult,
  PairSearchResult,
  ProjectSearchResult,
  SearchResult,
} from '../types';

interface GroupedResultsListProps {
  /** All search results (will be grouped by project) */
  results: SearchResult[];

  /** Index of currently focused item for keyboard navigation (indexes into results array) */
  focusedIndex: number;

  /** The focused result from keyboard navigation (for accurate matching) */
  focusedResult?: SearchResult;

  /** Callback when any result is selected */
  onSelectResult: (result: SearchResult) => void;

  /** Callback to toggle favorite status (now requires projectId) */
  onToggleFavorite: (
    type: 'pair' | 'arbitrage' | 'balancer' | 'project',
    projectId: number,
    itemId: number,
  ) => void;

  /** Callback to add entire project to watchlist (using composite keys) */
  onAddProjectToWatchlist: (items: ProjectItems) => void;

  /** Callback to remove entire project from watchlist (using composite keys) */
  onRemoveProjectFromWatchlist: (items: ProjectItems) => void;

  /** Set of favorited pair keys (format: "projectId-pairId") */
  favoritePairs: Set<string>;

  /** Set of favorited arbitrage keys (format: "projectId-arbitrageId") */
  favoriteArbitrages: Set<string>;

  /** Set of favorited balancer keys (format: "projectId-balancerId") */
  favoriteBalancers: Set<string>;

  /** Set of favorited project page IDs */
  favoriteProjects: Set<string>;

  /** Whether results are loading */
  isLoading: boolean;

  /** Search query for empty state */
  searchQuery: string;

  /** Symbol status map for ST zone display (CEX pairs only) */
  symbolStatuses?: SymbolStatusMap;

  /** Total pairs count per project (for "All pairs" badge - shows total, not filtered count) */
  projectTotalPairs: Map<number, number>;

  /**
   * Called when a project group is expanded (collapsed -> expanded).
   * Intended for lazy per-project data refresh to avoid fetching all projects at once.
   */
  onProjectExpand?: (projectId: number) => void;
}

const MAX_ITEMS_PER_PROJECT = 20;
const COLLAPSED_PROJECTS_STORAGE_KEY = 'mm-dashboard:pair-search:collapsed-projects';

const loadCollapsedProjects = (): Set<number> => {
  try {
    const stored = localStorage.getItem(COLLAPSED_PROJECTS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return new Set(parsed.filter((id): id is number => typeof id === 'number'));
      }
    }
  } catch {
    // Ignore parse errors
  }
  return new Set();
};

const saveCollapsedProjects = (collapsed: Set<number>): void => {
  try {
    localStorage.setItem(COLLAPSED_PROJECTS_STORAGE_KEY, JSON.stringify([...collapsed]));
  } catch {
    // Ignore storage errors
  }
};

interface ProjectGroup {
  projectId: number;
  projectName: string;
  items: SearchResult[];
  /** Composite keys for bulk watchlist operations */
  itemKeys: ProjectItems;
  projectLink: ProjectSearchResult;
}

interface ProjectHeaderProps {
  name: string;
  count: number;
  isCollapsed: boolean;
  isAllFavorited: boolean;
  onToggleCollapse: () => void;
  onToggleWatchlist: () => void;
}

const ProjectHeader: React.FC<ProjectHeaderProps> = ({
  name,
  count,
  isCollapsed,
  isAllFavorited,
  onToggleCollapse,
  onToggleWatchlist,
}) => {
  const handleCollapseKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggleCollapse();
    }
  };

  const handleWatchlistKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggleWatchlist();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="sticky top-0 z-10 flex items-center gap-3 bg-[rgba(24,24,28,0.98)] px-2 py-2 backdrop-blur-xl"
      role="heading"
      aria-level={3}
    >
      {/* Left content - matches items' px-3 */}
      <div className="flex min-w-0 flex-1 items-center gap-2 px-2 sm:px-3">
        <button
          type="button"
          tabIndex={0}
          onClick={onToggleCollapse}
          onKeyDown={handleCollapseKeyDown}
          aria-expanded={!isCollapsed}
          aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${name} project (${count} items)`}
          className="flex min-w-0 items-center gap-1.5 sm:gap-2 text-gray-400 transition-colors hover:text-white focus:outline-none focus-visible:text-indigo-300 focus-visible:ring-2 focus-visible:ring-indigo-500/50 rounded"
        >
          {isCollapsed ? (
            <RightOutlined className="shrink-0 text-[10px]" aria-hidden="true" />
          ) : (
            <DownOutlined className="shrink-0 text-[10px]" aria-hidden="true" />
          )}
          <FolderOutlined className="shrink-0 text-xs" aria-hidden="true" />
          <span className="truncate text-xs font-semibold uppercase tracking-wider">{name}</span>
        </button>

        <span
          className="shrink-0 rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] font-medium text-gray-500"
          aria-label={`${count} items`}
        >
          {count}
        </span>
      </div>

      {/* Right content - responsive width */}
      <div className="flex w-[70px] sm:w-[100px] shrink-0 items-center justify-end gap-2 sm:gap-3 pr-2 sm:pr-3">
        <button
          type="button"
          tabIndex={0}
          onClick={e => {
            e.stopPropagation();
            onToggleWatchlist();
          }}
          onKeyDown={handleWatchlistKeyDown}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-full transition-all duration-150',
            'hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50',
            isAllFavorited ? 'text-orange-400' : 'text-gray-500 hover:text-orange-300',
          )}
          aria-label={
            isAllFavorited
              ? `Remove all ${name} items from watchlist`
              : `Add all ${name} items to watchlist`
          }
          aria-pressed={isAllFavorited}
          title={isAllFavorited ? 'Remove all from watchlist' : 'Add all to watchlist'}
        >
          {isAllFavorited ? (
            <StarFilled className="text-sm" aria-hidden="true" />
          ) : (
            <StarOutlined className="text-sm" aria-hidden="true" />
          )}
        </button>
      </div>
    </motion.div>
  );
};

export const GroupedResultsList: React.FC<GroupedResultsListProps> = ({
  results,
  focusedIndex,
  focusedResult: _focusedResult, // Not used - keyboard navigation uses flatIndexMap
  onSelectResult,
  onToggleFavorite,
  onAddProjectToWatchlist,
  onRemoveProjectFromWatchlist,
  favoritePairs,
  favoriteArbitrages,
  favoriteBalancers,
  favoriteProjects,
  isLoading,
  searchQuery,
  symbolStatuses,
  projectTotalPairs,
  onProjectExpand,
}) => {
  // Track collapsed projects with persistence
  const [collapsedProjects, setCollapsedProjects] = useState<Set<number>>(loadCollapsedProjects);
  const defaultCollapseInitializedRef = useRef(false);

  // Persist collapsed state on change
  useEffect(() => {
    saveCollapsedProjects(collapsedProjects);
  }, [collapsedProjects]);

  const toggleCollapse = useCallback(
    (projectId: number) => {
      const isCurrentlyCollapsed = collapsedProjects.has(projectId);
      if (isCurrentlyCollapsed) {
        onProjectExpand?.(projectId);
      }

      setCollapsedProjects(prev => {
        const next = new Set(prev);
        if (next.has(projectId)) {
          next.delete(projectId);
        } else {
          next.add(projectId);
        }
        return next;
      });
    },
    [collapsedProjects, onProjectExpand],
  );

  // Group results by project with composite keys for bulk operations
  const projectGroups = useMemo((): ProjectGroup[] => {
    const groupsMap = new Map<number, ProjectGroup>();

    results.forEach(result => {
      const projectId = result.projectId;
      const projectName = result.type === 'project' ? result.name : result.projectName;

      let group = groupsMap.get(projectId);
      if (!group) {
        group = {
          projectId,
          projectName,
          items: [],
          itemKeys: { projectId, pairKeys: [], arbitrageKeys: [], balancerKeys: [] },
          projectLink: {
            id: -projectId,
            type: 'project',
            projectId,
            name: projectName,
            pairsCount: 0,
            status: 'active',
            matchScore: 0,
            matchType: 'substring',
          },
        };
        groupsMap.set(projectId, group);
      }

      if (result.type === 'project') {
        group.projectLink = result;
        return;
      }

      group.items.push(result);

      // Track composite keys for bulk watchlist operations
      if (result.type === 'pair') {
        group.itemKeys.pairKeys.push(createWatchlistKey(projectId, result.pairId));
      } else if (result.type === 'arbitrage') {
        group.itemKeys.arbitrageKeys.push(createWatchlistKey(projectId, result.arbitrageId));
      } else if (result.type === 'balancer') {
        group.itemKeys.balancerKeys.push(createWatchlistKey(projectId, result.balancerId));
      }
    });

    return Array.from(groupsMap.values())
      .filter(group => group.items.length > 0) // Only show groups with actual items
      .map(group => {
        // Count pairs in results for this group (for display in the badge)
        const pairsCount = group.items.filter(item => item.type === 'pair').length;
        const firstItem = group.items[0];
        group.projectLink = {
          ...group.projectLink,
          pairsCount,
          // Use pairsCount for determining status - if pairs exist, check if archived
          // For arbitrage/balancer-only groups, consider it active
          status:
            pairsCount > 0 && firstItem && 'isArchived' in firstItem && firstItem.isArchived
              ? 'archived'
              : 'active',
        };
        return group;
      })
      .sort((a, b) => a.projectName.localeCompare(b.projectName));
  }, [results]);

  // Default collapsed state: collapse all projects if nothing persisted
  useEffect(() => {
    const hasInitializedDefault = defaultCollapseInitializedRef.current;
    if (hasInitializedDefault) return;
    if (collapsedProjects.size === 0 && projectGroups.length > 0) {
      setCollapsedProjects(new Set(projectGroups.map(group => group.projectId)));
      defaultCollapseInitializedRef.current = true;
    }
  }, [collapsedProjects.size, projectGroups]);

  // Build flat index map for keyboard navigation based on VISUAL display order
  const flatIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    let index = 0;

    projectGroups.forEach(group => {
      if (!collapsedProjects.has(group.projectId)) {
        // Always include "All pairs" link for every project with results
        const projectLinkKey = `project-${group.projectLink.id}`;
        map.set(projectLinkKey, index);
        index++;

        // Then regular items
        group.items.slice(0, MAX_ITEMS_PER_PROJECT).forEach(item => {
          const key = `${item.type}-${item.id}`;
          map.set(key, index);
          index++;
        });
      }
    });

    return map;
  }, [projectGroups, collapsedProjects]);

  // Check if item is focused using the visual flat index map
  const isFocused = useCallback(
    (item: SearchResult | ProjectSearchResult): boolean => {
      if (focusedIndex < 0) return false;
      const key = `${item.type}-${item.id}`;
      return flatIndexMap.get(key) === focusedIndex;
    },
    [focusedIndex, flatIndexMap],
  );

  // Check favorite status using composite keys
  const isFavorite = (item: SearchResult): boolean => {
    switch (item.type) {
      case 'pair':
        return favoritePairs.has(createWatchlistKey(item.projectId, item.pairId));
      case 'arbitrage':
        return favoriteArbitrages.has(createWatchlistKey(item.projectId, item.arbitrageId));
      case 'balancer':
        return favoriteBalancers.has(createWatchlistKey(item.projectId, item.balancerId));
      default:
        return false;
    }
  };

  // Check ST zone status for CEX pairs (only mexc, kucoin, bybit support this)
  const getIsSt = useCallback(
    (item: PairSearchResult): boolean => {
      if (item.pairType !== 'cex' || !symbolStatuses) return false;
      const key = buildSymbolStatusKey(item.platform, item.quoteToken, item.baseToken);
      return symbolStatuses[key]?.isSt ?? false;
    },
    [symbolStatuses],
  );

  // Check if all items in a project are favorited (using composite keys)
  const isProjectFullyFavorited = useCallback(
    (group: ProjectGroup): boolean => {
      const { itemKeys } = group;
      const allPairsFav = itemKeys.pairKeys.every(key => favoritePairs.has(key));
      const allArbsFav = itemKeys.arbitrageKeys.every(key => favoriteArbitrages.has(key));
      const allBalsFav = itemKeys.balancerKeys.every(key => favoriteBalancers.has(key));
      return allPairsFav && allArbsFav && allBalsFav;
    },
    [favoritePairs, favoriteArbitrages, favoriteBalancers],
  );

  const handleToggleFavorite = (item: SearchResult | ProjectSearchResult) => {
    switch (item.type) {
      case 'pair':
        onToggleFavorite('pair', item.projectId, item.pairId);
        break;
      case 'arbitrage':
        onToggleFavorite('arbitrage', item.projectId, item.arbitrageId);
        break;
      case 'balancer':
        onToggleFavorite('balancer', item.projectId, item.balancerId);
        break;
      case 'project':
        onToggleFavorite('project', item.projectId, item.projectId);
        break;
    }
  };

  const isProjectFavorite = (projectId: number): boolean => {
    return favoriteProjects.has(String(projectId));
  };

  const handleToggleProjectWatchlist = (group: ProjectGroup) => {
    if (isProjectFullyFavorited(group)) {
      onRemoveProjectFromWatchlist(group.itemKeys);
    } else {
      onAddProjectToWatchlist(group.itemKeys);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <LoadingOutlined className="mb-3 text-3xl text-indigo-400" spin />
        <span className="text-sm">Loading results...</span>
      </div>
    );
  }

  // Empty state
  if (results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/5">
          <SearchOutlined className="text-2xl text-gray-500" />
        </div>
        <div className="mb-1 text-base font-medium text-gray-300">No results found</div>
        {searchQuery ? (
          <div className="max-w-[200px] text-sm text-gray-500">
            Try a different search term or adjust the filters
          </div>
        ) : (
          <div className="text-sm text-gray-500">Start typing to search</div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'max-h-[380px] overflow-y-auto pb-2',
        'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/10 hover:scrollbar-thumb-white/20',
      )}
      id="search-results-listbox"
      role="listbox"
      aria-label="Search results"
    >
      <AnimatePresence mode="popLayout">
        {projectGroups.map((group, groupIndex) => {
          const isCollapsed = collapsedProjects.has(group.projectId);
          // Always show "All pairs" link for every project with results
          const displayedItems = [
            group.projectLink,
            ...group.items.slice(0, MAX_ITEMS_PER_PROJECT),
          ];
          const isAllFavorited = isProjectFullyFavorited(group);

          return (
            <motion.div
              key={`project-${group.projectId}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ delay: groupIndex * 0.02 }}
            >
              <ProjectHeader
                name={group.projectName}
                count={group.items.length}
                isCollapsed={isCollapsed}
                isAllFavorited={isAllFavorited}
                onToggleCollapse={() => toggleCollapse(group.projectId)}
                onToggleWatchlist={() => handleToggleProjectWatchlist(group)}
              />

              <AnimatePresence>
                {!isCollapsed && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden px-2"
                  >
                    {displayedItems.map((item, itemIndex) => {
                      // Check focus for all items including project links
                      const itemIsFocused = isFocused(item);
                      const itemIsFavorite = item.type === 'project' ? false : isFavorite(item);

                      if (item.type === 'project') {
                        const projectIsFavorite = isProjectFavorite(item.projectId);
                        // Use total pairs count from all pairs, not filtered count
                        const totalPairsInProject = projectTotalPairs.get(item.projectId) ?? 0;
                        return (
                          <motion.div
                            key={`project-link-${item.projectId}`}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: groupIndex * 0.02 + itemIndex * 0.01 }}
                          >
                            <div
                              id={`result-project-${item.id}`}
                              role="option"
                              aria-selected={itemIsFocused}
                              tabIndex={0}
                              onClick={() => onSelectResult(item)}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onSelectResult(item);
                                }
                              }}
                              className={cn(
                                'flex w-full cursor-pointer items-center justify-between rounded-xl px-3 py-2.5 text-left text-[#eef1ff] transition-all duration-150 hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50',
                                itemIsFocused && 'bg-white/10 ring-1 ring-indigo-500/50',
                              )}
                              aria-label={`All pairs for ${group.projectName}, ${totalPairsInProject} pairs`}
                            >
                              <div className="flex flex-1 items-center gap-3" aria-hidden="true">
                                <div
                                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/8 text-gray-200"
                                  aria-hidden="true"
                                >
                                  <AppstoreAddOutlined />
                                </div>
                                <div className="flex flex-col text-left">
                                  <span className="text-sm font-semibold text-[#f6f7ff]">
                                    All pairs
                                  </span>
                                  <span className="text-xs text-[#9ca3af]">
                                    {group.projectName}
                                  </span>
                                </div>
                              </div>
                              {/* Right content - responsive width */}
                              <div className="flex w-[70px] sm:w-[100px] shrink-0 items-center justify-end gap-2">
                                {/* Pairs count badge - shows TOTAL pairs, not filtered */}
                                <span
                                  className="hidden sm:inline rounded-lg bg-indigo-500/20 px-2 py-1 text-center text-[10px] font-semibold text-indigo-300"
                                  aria-label={`${totalPairsInProject} pairs`}
                                >
                                  {totalPairsInProject}
                                </span>
                                {/* Watchlist star */}
                                <button
                                  type="button"
                                  tabIndex={0}
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleToggleFavorite(item);
                                  }}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleToggleFavorite(item);
                                    }
                                  }}
                                  className={cn(
                                    'flex h-7 w-7 items-center justify-center rounded-full transition-all duration-150',
                                    'hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50',
                                    projectIsFavorite
                                      ? 'text-orange-400'
                                      : 'text-gray-500 hover:text-orange-300',
                                  )}
                                  aria-label={
                                    projectIsFavorite
                                      ? `Remove ${group.projectName} all pairs from watchlist`
                                      : `Add ${group.projectName} all pairs to watchlist`
                                  }
                                  aria-pressed={projectIsFavorite}
                                >
                                  {projectIsFavorite ? (
                                    <StarFilled className="text-sm" aria-hidden="true" />
                                  ) : (
                                    <StarOutlined className="text-sm" aria-hidden="true" />
                                  )}
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        );
                      }

                      return (
                        <motion.div
                          key={`${item.type}-${item.id}`}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: groupIndex * 0.02 + itemIndex * 0.01 }}
                        >
                          {item.type === 'pair' && (
                            <SearchResultItem
                              result={item as PairSearchResult}
                              isFocused={itemIsFocused}
                              isFavorite={itemIsFavorite}
                              isSt={getIsSt(item as PairSearchResult)}
                              onClick={() => onSelectResult(item)}
                              onToggleFavorite={() => handleToggleFavorite(item)}
                            />
                          )}
                          {item.type === 'arbitrage' && (
                            <ArbitrageResultItem
                              result={item as ArbitrageSearchResult}
                              isFocused={itemIsFocused}
                              isFavorite={itemIsFavorite}
                              onClick={() => onSelectResult(item)}
                              onToggleFavorite={() => handleToggleFavorite(item)}
                            />
                          )}
                          {item.type === 'balancer' && (
                            <BalancerResultItem
                              result={item as BalancerSearchResult}
                              isFocused={itemIsFocused}
                              isFavorite={itemIsFavorite}
                              onClick={() => onSelectResult(item)}
                              onToggleFavorite={() => handleToggleFavorite(item)}
                            />
                          )}
                        </motion.div>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};
